import Box from '@mui/material/Box';
import { IndentedSections } from '@openx/components-core/lib/IndentedSection/IndentedSections.tsx';
import type { TargetingReadOnlyProps } from '@openx/types/targeting/targeting.ts';
import { Criteria } from '@openx/types/targeting/targetingValuesTypes.ts';
import { type VideoFormState, VideoOption } from '@openx/types/targeting/video.ts';
import omit from 'lodash/omit';
import { Fragment, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getChipLabelVisibilityFlags } from '../../utils/hooks/getChipLabelVisibilityFlags.ts';
import { useParameterRemove } from '../../utils/hooks/useParameterRemove.ts';
import { useTargetingDisabledState } from '../../utils/hooks/useTargetingDisabledState.ts';
import { DimensionChipLine } from '../shared/DimensionChip/DimensionChipLine.tsx';
import { useGetFilteredFields } from '../shared/hooks/useGetFilteredFields.ts';
import { useDropdownOpen } from '../shared/ReadOnlyTargetingDropdown/hooks/useDropdownOpen.ts';
import { StyledReadOnlyTargetingDropdown } from '../shared/ReadOnlyTargetingDropdown/ReadOnlyTargetingDropdown.ts';
import { TargetingDropdownSection } from '../shared/ReadOnlyTargetingDropdown/TargetingDropdownSection.tsx';
import { TargetingReadOnly } from '../shared/TargetingReadOnly/TargetingReadOnly.tsx';
import { MaxAdDurationItem } from './components/MaxAdDuration/MaxAdDurationItem.tsx';
import { PlacementAndFormat } from './components/PlacementAndFormat.tsx';
import { RewardedVideoItem } from './components/RewardedVideo/RewardedVideoItem.tsx';
import { VideoSkipabilityItem } from './components/VideoSkipability/VideoSkipabilityItem.tsx';
import { placementAndFormat } from './constants/constants.ts';
import { useVideoTypeOptions } from './hooks/useVideoTypeOptions.ts';
import { Items } from './Items.tsx';
import { isEmptyState } from './utils/utils.ts';

export const VideoReadOnly = ({
  name,
  field,
  userChangedInitialValue,
  onOpenDrawerClick,
  onRemoveClick,
  targetingParams,
  readonly,
  dataTest,
  onRemoveParameter,
}: TargetingReadOnlyProps<VideoFormState> & {
  onRemoveParameter?: (opt: VideoOption) => void;
  field: string;
  userChangedInitialValue: boolean;
}) => {
  const { isDisabledOrHasError, disabledReasonMessage, errorReasonMessage } = useTargetingDisabledState(field);

  const { t } = useTranslation();
  const { getOptionLabel } = useVideoTypeOptions();

  const targetingFields = omit(targetingParams, [
    VideoOption.MAX_AD_DURATION,
    VideoOption.REWARDED_VIDEO,
    VideoOption.VIDEO_SKIPABILITY,
  ]);

  const payloadVideoTypes = useGetFilteredFields(targetingFields || {});
  const hasBothPlcmntFormat = placementAndFormat.every(type => payloadVideoTypes.includes(type));

  const videoTypes = useMemo(
    () =>
      hasBothPlcmntFormat
        ? payloadVideoTypes.filter(type => type !== VideoOption.PLACEMENT && type !== VideoOption.PRESENTATION_FORMAT)
        : payloadVideoTypes,
    [hasBothPlcmntFormat, payloadVideoTypes]
  );

  const inCategoryOrder = useMemo(
    () => [
      VideoOption.IN_STREAM_VIDEO_POSITION,
      VideoOption.MAX_AD_DURATION,
      VideoOption.PRESENTATION_FORMAT,
      VideoOption.PLACEMENT,
      VideoOption.REWARDED_VIDEO,
      VideoOption.VIDEO_SKIPABILITY,
    ],
    []
  );

  const inCategoryTypesToRender = useMemo(() => inCategoryOrder, [inCategoryOrder]);

  const selectedVideoOptions = useMemo(() => {
    let result = 0;
    if (!targetingParams) return result;

    if (targetingParams[VideoOption.REWARDED_VIDEO] !== null) result++;
    if (targetingParams[VideoOption.MAX_AD_DURATION] !== null) result++;
    if (targetingParams[VideoOption.VIDEO_SKIPABILITY] !== null) result++;
    if (
      targetingParams[VideoOption.PRESENTATION_FORMAT]?.val &&
      targetingParams[VideoOption.PRESENTATION_FORMAT].val.size > 0
    )
      result++;
    if (
      targetingParams[VideoOption.IN_STREAM_VIDEO_POSITION]?.val &&
      targetingParams[VideoOption.IN_STREAM_VIDEO_POSITION].val.size > 0
    )
      result++;
    if (targetingParams[VideoOption.PLACEMENT]?.val && targetingParams[VideoOption.PLACEMENT].val.size > 0) result++;

    return result;
  }, [targetingParams]);

  const hasValue = selectedVideoOptions > 0;
  const { isOpen, toggleOpen } = useDropdownOpen(hasValue);

  const parameterRemoveHandler = useParameterRemove<VideoOption>(onRemoveParameter, onRemoveClick);

  const hasValueForType = useCallback(
    (type: VideoOption): boolean => {
      if (!targetingParams) return false;

      switch (type) {
        case VideoOption.MAX_AD_DURATION:
        case VideoOption.REWARDED_VIDEO:
        case VideoOption.VIDEO_SKIPABILITY:
          return Boolean(targetingParams[type]);
        default:
          return Boolean(targetingParams[type]?.val?.size);
      }
    },
    [targetingParams]
  );

  const labelVisibilityFlags = useMemo(
    () => getChipLabelVisibilityFlags(inCategoryTypesToRender, hasValueForType),
    [inCategoryTypesToRender, hasValueForType]
  );

  const showLabelAfterType = useCallback(
    (type: VideoOption) => {
      const idx = inCategoryTypesToRender.indexOf(type);
      return idx !== -1 ? Boolean(labelVisibilityFlags[idx]) : false;
    },
    [inCategoryTypesToRender, labelVisibilityFlags]
  );

  if (!readonly)
    return (
      <TargetingReadOnly
        name={name}
        field={field}
        readonly={readonly}
        isEmpty={isEmptyState(targetingParams)}
        onOpenDrawerClick={onOpenDrawerClick}
        onRemove={onRemoveClick}
        isDisabled={isDisabledOrHasError}
        dataTest={dataTest}
        description={t(
          'Control where and how video ads are shown by setting specific creative and placement criteria within the video environment.'
        )}
        optionsCount={selectedVideoOptions}
        isOpen={isOpen}
        toggleOpen={toggleOpen}
        disabledReasonMessage={disabledReasonMessage}
      >
        {isOpen && (
          <StyledReadOnlyTargetingDropdown>
            {inCategoryTypesToRender.map((type, index) => {
              const showSeparator = index < inCategoryTypesToRender.length - 1;

              if (hasBothPlcmntFormat && type === VideoOption.PLACEMENT) {
                return null;
              }

              if (
                type === VideoOption.IN_STREAM_VIDEO_POSITION ||
                type === VideoOption.PLACEMENT ||
                type === VideoOption.PRESENTATION_FORMAT
              ) {
                const isEmpty = !targetingParams?.[type]?.val?.size;

                const isPresentationFormat = type === VideoOption.PRESENTATION_FORMAT;
                const shouldRenderOrGroup = hasBothPlcmntFormat && isPresentationFormat;

                if (shouldRenderOrGroup) {
                  return (
                    <Fragment key={type}>
                      <IndentedSections dataTest="placement-and-format-or-container">
                        <Box sx={{ pl: 1.5 }}>
                          <TargetingDropdownSection
                            name={getOptionLabel(VideoOption.PRESENTATION_FORMAT)}
                            isEmpty={Boolean(!targetingParams?.[VideoOption.PRESENTATION_FORMAT]?.val?.size)}
                            onOpenDrawerClick={() => onOpenDrawerClick?.(VideoOption.PRESENTATION_FORMAT)}
                            onRemove={parameterRemoveHandler(VideoOption.PRESENTATION_FORMAT)}
                            isDisabled={isDisabledOrHasError}
                            hasOptionsSelected={hasValue}
                            disabledReasonMessage={disabledReasonMessage}
                          >
                            {targetingParams && !!targetingParams[VideoOption.PRESENTATION_FORMAT]?.val.size && (
                              <div key={VideoOption.PRESENTATION_FORMAT}>
                                <Items
                                  params={targetingParams[VideoOption.PRESENTATION_FORMAT]}
                                  type={VideoOption.PRESENTATION_FORMAT}
                                  disabledReasonMessage={errorReasonMessage}
                                />
                              </div>
                            )}
                          </TargetingDropdownSection>

                          <DimensionChipLine
                            targetingDimension={Criteria.ANY}
                            showLabel={
                              hasValueForType(VideoOption.PRESENTATION_FORMAT) && hasValueForType(VideoOption.PLACEMENT)
                            }
                            isSubsection
                          />

                          <TargetingDropdownSection
                            name={getOptionLabel(VideoOption.PLACEMENT)}
                            isEmpty={Boolean(!targetingParams?.[VideoOption.PLACEMENT]?.val?.size)}
                            onOpenDrawerClick={() => onOpenDrawerClick?.(VideoOption.PLACEMENT)}
                            onRemove={parameterRemoveHandler(VideoOption.PLACEMENT)}
                            isDisabled={isDisabledOrHasError}
                            hasOptionsSelected={hasValue}
                            disabledReasonMessage={disabledReasonMessage}
                          >
                            {targetingParams && !!targetingParams[VideoOption.PLACEMENT]?.val.size && (
                              <div key={VideoOption.PLACEMENT}>
                                <Items
                                  params={targetingParams[VideoOption.PLACEMENT]}
                                  type={VideoOption.PLACEMENT}
                                  disabledReasonMessage={errorReasonMessage}
                                />
                              </div>
                            )}
                          </TargetingDropdownSection>
                        </Box>
                      </IndentedSections>

                      {showSeparator ? (
                        <DimensionChipLine showLabel={showLabelAfterType(VideoOption.PLACEMENT)} />
                      ) : null}
                    </Fragment>
                  );
                }

                return (
                  <Fragment key={type}>
                    <TargetingDropdownSection
                      name={getOptionLabel(type)}
                      isEmpty={Boolean(isEmpty)}
                      onOpenDrawerClick={() => onOpenDrawerClick?.(type)}
                      onRemove={parameterRemoveHandler(type)}
                      isDisabled={isDisabledOrHasError}
                      hasOptionsSelected={hasValue}
                      disabledReasonMessage={disabledReasonMessage}
                    >
                      {targetingParams && !!targetingParams[type]?.val.size && (
                        <div key={type}>
                          <Items
                            params={targetingParams[type]}
                            type={type}
                            disabledReasonMessage={errorReasonMessage}
                          />
                        </div>
                      )}
                    </TargetingDropdownSection>

                    {showSeparator ? <DimensionChipLine showLabel={labelVisibilityFlags[index] ?? false} /> : null}
                  </Fragment>
                );
              }

              if (type === VideoOption.MAX_AD_DURATION) {
                const maxAdDuration = targetingParams?.[VideoOption.MAX_AD_DURATION];
                const isEmpty = !maxAdDuration;

                return (
                  <Fragment key={type}>
                    <TargetingDropdownSection
                      name={getOptionLabel(VideoOption.MAX_AD_DURATION)}
                      isEmpty={Boolean(isEmpty)}
                      onOpenDrawerClick={() => onOpenDrawerClick?.(VideoOption.MAX_AD_DURATION)}
                      onRemove={parameterRemoveHandler(VideoOption.MAX_AD_DURATION)}
                      isDisabled={isDisabledOrHasError}
                      hasOptionsSelected={hasValue}
                      disabledReasonMessage={disabledReasonMessage}
                    >
                      {maxAdDuration && (
                        <MaxAdDurationItem
                          params={maxAdDuration}
                          shouldAddDimensionLine={false}
                          disabledReasonMessage={errorReasonMessage}
                        />
                      )}
                    </TargetingDropdownSection>

                    {showSeparator ? <DimensionChipLine showLabel={labelVisibilityFlags[index] ?? false} /> : null}
                  </Fragment>
                );
              }

              if (type === VideoOption.REWARDED_VIDEO) {
                const rewardedVideo = targetingParams?.[VideoOption.REWARDED_VIDEO];
                const isEmpty = !rewardedVideo;

                return (
                  <Fragment key={type}>
                    <TargetingDropdownSection
                      name={getOptionLabel(VideoOption.REWARDED_VIDEO)}
                      isEmpty={Boolean(isEmpty)}
                      onOpenDrawerClick={() => onOpenDrawerClick?.(VideoOption.REWARDED_VIDEO)}
                      onRemove={parameterRemoveHandler(VideoOption.REWARDED_VIDEO)}
                      isDisabled={isDisabledOrHasError}
                      hasOptionsSelected={hasValue}
                      disabledReasonMessage={disabledReasonMessage}
                    >
                      {rewardedVideo && !isEmpty && (
                        <RewardedVideoItem
                          params={rewardedVideo}
                          shouldAddDimensionLine={false}
                          disabledReasonMessage={errorReasonMessage}
                        />
                      )}
                    </TargetingDropdownSection>

                    {showSeparator ? <DimensionChipLine showLabel={labelVisibilityFlags[index] ?? false} /> : null}
                  </Fragment>
                );
              }

              if (type === VideoOption.VIDEO_SKIPABILITY) {
                const videoSkipability = targetingParams?.[VideoOption.VIDEO_SKIPABILITY];
                const isEmpty = !videoSkipability;

                return (
                  <Fragment key={type}>
                    <TargetingDropdownSection
                      name={getOptionLabel(VideoOption.VIDEO_SKIPABILITY)}
                      isEmpty={Boolean(isEmpty)}
                      onOpenDrawerClick={() => onOpenDrawerClick?.(VideoOption.VIDEO_SKIPABILITY)}
                      onRemove={parameterRemoveHandler(VideoOption.VIDEO_SKIPABILITY)}
                      isDisabled={isDisabledOrHasError}
                      hasOptionsSelected={hasValue}
                      disabledReasonMessage={disabledReasonMessage}
                    >
                      {videoSkipability && !isEmpty && (
                        <VideoSkipabilityItem params={videoSkipability} disabledReasonMessage={errorReasonMessage} />
                      )}
                    </TargetingDropdownSection>

                    {showSeparator ? <DimensionChipLine showLabel={labelVisibilityFlags[index] ?? false} /> : null}
                  </Fragment>
                );
              }

              return null;
            })}
          </StyledReadOnlyTargetingDropdown>
        )}
      </TargetingReadOnly>
    );

  const rewardedVideo = targetingParams?.[VideoOption.REWARDED_VIDEO];
  const maxAdDuration = targetingParams?.[VideoOption.MAX_AD_DURATION];
  const videoSkipability = targetingParams?.[VideoOption.VIDEO_SKIPABILITY];

  return (
    <TargetingReadOnly
      name={name}
      field={field}
      userChangedInitialValue={userChangedInitialValue}
      readonly={readonly}
      isEmpty={isEmptyState(targetingParams)}
      onOpenDrawerClick={onOpenDrawerClick}
      onRemove={onRemoveClick}
      isDisabled={isDisabledOrHasError}
      dataTest={dataTest}
      description={t(
        'Control where and how video ads are shown by setting specific creative and placement criteria within the video environment.'
      )}
      disabledReasonMessage={disabledReasonMessage}
    >
      {targetingParams && !isEmptyState(targetingParams) && (
        <>
          {videoTypes.map((type, index) => {
            const shouldAddDimensionChipLine =
              videoTypes.length - 1 > index ||
              !!rewardedVideo ||
              !!videoSkipability ||
              !!maxAdDuration ||
              hasBothPlcmntFormat;

            return (
              !!targetingParams[type].val.size && (
                <div key={type}>
                  <Items params={targetingParams[type]} type={type} disabledReasonMessage={errorReasonMessage} />
                  {shouldAddDimensionChipLine && <DimensionChipLine />}
                </div>
              )
            );
          })}

          {hasBothPlcmntFormat && (
            <PlacementAndFormat
              placementAndFormat={placementAndFormat}
              params={targetingParams}
              readonly
              shouldAddDimensionLine={!!rewardedVideo}
              disabledReasonMessage={errorReasonMessage}
            />
          )}
          {!!maxAdDuration && (
            <MaxAdDurationItem
              params={maxAdDuration}
              shouldAddDimensionLine={!!rewardedVideo}
              disabledReasonMessage={errorReasonMessage}
            />
          )}
          {!!rewardedVideo && (
            <RewardedVideoItem
              params={rewardedVideo}
              shouldAddDimensionLine={!!videoSkipability}
              disabledReasonMessage={errorReasonMessage}
            />
          )}
          {!!videoSkipability && (
            <VideoSkipabilityItem params={videoSkipability} disabledReasonMessage={errorReasonMessage} />
          )}
        </>
      )}
    </TargetingReadOnly>
  );
};
